import { t } from "i18next";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import RecorderForm, {
  type RecorderFormHandle,
} from "@/components/ui/audio-recorder/recorder-form";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/hooks/use-toast";
import { hasPublicRecordingIdentity } from "@/lib/offline-recording/identity";
import type { OfflineVisitForm } from "@/lib/offline-recording/types";
import { useVisitScope } from "@/lib/visit-queries";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import {
  currentOfflineAccount,
  recoverableRecordings,
  useOfflineRecordingStore,
} from "@/store/offlineRecording.store";
import {
  formatRecordingDuration,
  formatRecordingStart,
  formatSize,
} from "./format";
import {
  RecoveryDialogView,
  SharedComputerNote,
} from "./OfflineRecordingViews";

export const toOfflineVisitForm = (
  form: RecorderFormSchemaType,
  {
    instructions,
    skipShortcuts,
    recordingConsent,
  }: {
    instructions?: string[];
    skipShortcuts?: string[];
    recordingConsent?: true;
  } = {},
): OfflineVisitForm => ({
  ...(form.title?.trim() ? { title: form.title.trim() } : {}),
  template: form.template,
  templateName: form.templateName,
  customTemplatePublicId: form.customTemplatePublicId,
  style: form.style,
  format: form.format,
  examination: form.examination,
  ...(instructions !== undefined ? { instructions } : {}),
  ...(skipShortcuts?.length ? { skipShortcuts } : {}),
  ...(recordingConsent ? { recordingConsent } : {}),
});

const failure = (error: unknown): void => {
  toast({
    variant: "error",
    title: t("offlineRecording.errors.title", "Recording not changed"),
    description:
      error instanceof Error
        ? error.message
        : t(
            "offlineRecording.errors.storage",
            "The recording on this device could not be updated.",
          ),
  });
};

/** Asks for the visit form of a recovered recording, then queues it. */
export const ProcessRecordingSheet = (): JSX.Element => {
  const processingId = useOfflineRecordingStore((state) => state.processingId);
  const recording = useOfflineRecordingStore((state) =>
    state.recordings.find((row) => row.id === state.processingId),
  );
  const online = useOfflineRecordingStore((state) => state.online);
  const formRef = useRef<RecorderFormHandle>(null);
  const [sending, setSending] = useState(false);
  const readScope = useVisitScope();
  useEffect(() => {
    if (currentOfflineAccount() === readScope) setSending(false);
  }, [readScope]);
  const close = (): void =>
    useOfflineRecordingStore.getState().setProcessing(null);

  const submit = async (form: RecorderFormSchemaType): Promise<void> => {
    if (!recording) return;
    const scope = readScope;
    if (!scope || currentOfflineAccount() !== scope) return;
    setSending(true);
    try {
      await useOfflineRecordingStore
        .getState()
        .enqueue(recording.id, toOfflineVisitForm(form));
      if (currentOfflineAccount() !== scope) return;
      close();
      toast({
        description: online
          ? t(
              "offlineRecording.processing",
              "Uploading the recording. The note will be ready in a few minutes.",
            )
          : t(
              "offlineRecording.savedOffline",
              "Saved on this device. It will upload automatically when you're back online.",
            ),
      });
    } catch (error) {
      if (currentOfflineAccount() === scope) failure(error);
    } finally {
      if (currentOfflineAccount() === scope) setSending(false);
    }
  };

  return (
    <Sheet
      open={processingId !== null && !!recording}
      onOpenChange={(open) => {
        if (!open && !sending) close();
      }}
    >
      <SheetContent
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md sm:rounded-l-2xl"
        data-testid="process-recording-sheet"
      >
        <SheetHeader className="space-y-2 px-6 pb-4 pt-6 text-left sm:px-8">
          <SheetTitle className="pr-10 text-xl">
            {t("offlineRecording.process.title", "Process recording")}
          </SheetTitle>
          <SheetDescription className="text-sm leading-6">
            {recording
              ? t(
                  "offlineRecording.process.description",
                  "Recorded {{start}} · {{duration}} · {{size}}. Choose the note settings; the transcript is made from the full audio.",
                  {
                    start: formatRecordingStart(recording.createdAt),
                    duration: formatRecordingDuration(recording.durationMs),
                    size: formatSize(recording.bytes),
                  },
                )
              : null}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 sm:px-8">
          {recording && (
            <RecorderForm
              showTitle
              ref={formRef}
              initialValues={
                hasPublicRecordingIdentity(recording)
                  ? recording.form
                  : undefined
              }
              onSubmitData={submit}
            />
          )}
          <SharedComputerNote />
        </div>
        <SheetFooter className="flex flex-col gap-2 bg-tinted-grey-1 px-6 py-5 sm:flex-col sm:space-x-0 sm:rounded-bl-2xl sm:px-8">
          <SubscriptionOnlyButton
            className="w-full"
            disabled={sending}
            size="xl"
            validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
            onClick={async () => {
              await formRef.current?.submitForm();
            }}
          >
            {sending
              ? `${t("visits.sidebar.processing")}...`
              : t("offlineRecording.actions.process", "Process")}
          </SubscriptionOnlyButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Mounted once per page (Layout): starts the queue,
 * offers recovered recordings and hosts the Process sheet.
 */
export const OfflineRecordingCenter = (): JSX.Element => {
  const recordings = useOfflineRecordingStore((state) => state.recordings);
  const held = useOfflineRecordingStore((state) => state.held);
  const online = useOfflineRecordingStore((state) => state.online);
  const dismissed = useOfflineRecordingStore(
    (state) => state.recoveryDismissed,
  );
  const processingId = useOfflineRecordingStore((state) => state.processingId);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    useOfflineRecordingStore.getState().init();
  }, []);

  const recovered = recoverableRecordings(recordings, held);
  const store = useOfflineRecordingStore.getState;
  const busy = async (id: string, work: () => Promise<void>): Promise<void> => {
    setBusyId(id);
    try {
      await work();
    } catch (error) {
      failure(error);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <RecoveryDialogView
        busyId={busyId}
        online={online}
        open={recovered.length > 0 && !dismissed && processingId === null}
        recordings={recovered}
        onDiscard={(id) => void busy(id, () => store().discard(id))}
        onDownload={(id) => void busy(id, () => store().download(id))}
        onLater={() => store().dismissRecovery()}
        onProcess={(id) => store().setProcessing(id)}
        onRetry={(id) => void busy(id, () => store().retry(id))}
      />
      <ProcessRecordingSheet />
    </>
  );
};
